// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#276) — classify an EventLog row's DIRECTION for the
// glanceable timeline.
//
// Every change to an ActiveQueue document is either:
//   • OUT — a human action in the Scheduler UI that Scheduler wrote and then
//     pushed toward FileMaker (source === "Scheduler-UI").
//   • IN  — a push-back that arrived FROM FileMaker / an external process
//     (any other source, e.g. "External", or a missing source).
//
// This is the load-bearing signal that makes the timeline readable at a
// glance: user actions and automated system actions interleaved in true time
// order, each visually tagged with which way the data flowed. Extracted as a
// pure, tested function (per the project's "extract critical display logic"
// rule) so the timeline renderer and its tests share ONE implementation.
//
// Reference: dbtools/src/queries/invoice-2542494-timemachine.mjs — "direction
// from source (Scheduler-UI = user/out, External = system/in)".
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const SCHEDULER_UI = "scheduler-ui";

/**
 * @param {object} event — an EventLog row (needs `.source`)
 * @returns {{ dir: "out"|"in", actor: "user"|"system", label: string,
 *             arrow: string, flow: string }}
 */
export function classifyEventDirection(event) {
  const raw = event && event.source != null ? String(event.source).trim() : "";
  const isUiWrite = raw.toLowerCase() === SCHEDULER_UI;

  if (isUiWrite) {
    return {
      dir: "out",
      actor: "user",
      label: "Scheduler write",
      arrow: "↑", // ↑ up-and-out toward FileMaker
      flow: "Scheduler → FileMaker",
    };
  }

  return {
    dir: "in",
    actor: "system",
    // "External" is the common non-UI source; surface the raw value when it's
    // something else so a support user can see the true origin.
    label: raw && raw.toLowerCase() !== "external" ? `External (${raw})` : "FileMaker push-back",
    arrow: "↓", // ↓ down-and-in from FileMaker
    flow: "FileMaker → Scheduler",
  };
}

export const __testables__ = { SCHEDULER_UI };
